import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import styles from './Interactions.module.css';

export type TabItem<T extends string> = {
  id: T;
  label: ReactNode;
  disabled?: boolean;
};

export function Tabs<T extends string>({
  id,
  label,
  value,
  items,
  onChange,
  variant = 'line',
  className,
}: {
  id?: string;
  label: string;
  value: T;
  items: readonly TabItem<T>[];
  onChange: (value: T) => void;
  variant?: 'line' | 'compact';
  className?: string;
}): ReactNode {
  const generatedId = useId();
  const tabsId = id ?? generatedId;

  const moveFocus = (
    event: KeyboardEvent<HTMLButtonElement>,
    currentIndex: number,
  ): void => {
    const enabled = items
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => !item.disabled);
    if (enabled.length === 0) return;
    const enabledIndex = enabled.findIndex(({ index }) => index === currentIndex);
    let next: number;

    if (event.key === 'ArrowRight') next = (enabledIndex + 1) % enabled.length;
    else if (event.key === 'ArrowLeft') next = (enabledIndex - 1 + enabled.length) % enabled.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = enabled.length - 1;
    else return;

    event.preventDefault();
    const target = enabled[next];
    if (!target) return;
    onChange(target.item.id);
    document.getElementById(`${tabsId}-tab-${target.item.id}`)?.focus();
  };

  return (
    <div
      className={[
        styles.tabs,
        variant === 'compact' ? styles.tabsCompact : styles.tabsLine,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      role="tablist"
      aria-label={label}
    >
      {items.map((item, index) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          id={`${tabsId}-tab-${item.id}`}
          aria-selected={item.id === value}
          aria-controls={`${tabsId}-panel-${item.id}`}
          tabIndex={item.id === value ? 0 : -1}
          disabled={item.disabled}
          onClick={() => {
            onChange(item.id);
          }}
          onKeyDown={(event) => {
            moveFocus(event, index);
          }}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

export const IconButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label'> & {
    label: string;
    icon?: 'more';
  }
>(function IconButton(
  { label, icon = 'more', className, ...rest },
  ref,
): ReactNode {
  return (
    <button
      ref={ref}
      type="button"
      className={[styles.iconButton, className].filter(Boolean).join(' ')}
      aria-label={label}
      title={label}
      {...rest}
    >
      <Icon name={icon} />
    </button>
  );
});

function Icon({ name }: { name: 'more' }): ReactNode {
  return (
    <svg
      data-icon={name}
      viewBox="0 0 20 20"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="4" cy="10" r="1.4" />
      <circle cx="10" cy="10" r="1.4" />
      <circle cx="16" cy="10" r="1.4" />
    </svg>
  );
}

const CloseMenuContext = createContext<(() => void) | undefined>(undefined);

export function ActionMenu({
  label,
  children,
  align = 'end',
}: {
  label: string;
  children: ReactNode;
  align?: 'start' | 'end';
}): ReactNode {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  const close = (): void => {
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    rootRef.current
      ?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')
      ?.focus();

    const closeWhenOutside = (event: Event): void => {
      if (!rootRef.current?.contains(event.target as Node)) close();
    };

    document.addEventListener('pointerdown', closeWhenOutside);
    document.addEventListener('focusin', closeWhenOutside);
    return () => {
      document.removeEventListener('pointerdown', closeWhenOutside);
      document.removeEventListener('focusin', closeWhenOutside);
    };
  }, [open]);

  const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      triggerRef.current?.focus();
      return;
    }

    const items = Array.from(
      rootRef.current?.querySelectorAll<HTMLElement>(
        '[role="menuitem"]:not(:disabled)',
      ) ?? [],
    );
    const current = items.indexOf(document.activeElement as HTMLElement);
    if (items.length === 0) return;
    let next: number;
    if (event.key === 'ArrowDown') next = (current + 1) % items.length;
    else if (event.key === 'ArrowUp') next = (current - 1 + items.length) % items.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = items.length - 1;
    else return;

    event.preventDefault();
    items[next]?.focus();
  };

  return (
    <div className={styles.menuRoot} ref={rootRef}>
      <IconButton
        ref={triggerRef}
        label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => {
          setOpen((current) => !current);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
          }
        }}
      />
      {open ? (
        <CloseMenuContext.Provider value={close}>
          <div
            id={menuId}
            className={[
              styles.menu,
              align === 'start' ? styles.menuStart : styles.menuEnd,
            ].join(' ')}
            role="menu"
            onKeyDown={handleMenuKeyDown}
          >
            {children}
          </div>
        </CloseMenuContext.Provider>
      ) : null}
    </div>
  );
}

export function ActionMenuItem({
  children,
  onSelect,
  tone = 'neutral',
  disabled = false,
}: {
  children: ReactNode;
  onSelect: () => void | Promise<void>;
  tone?: 'neutral' | 'danger';
  disabled?: boolean;
}): ReactNode {
  const close = useContext(CloseMenuContext);
  return (
    <button
      type="button"
      role="menuitem"
      tabIndex={-1}
      className={[styles.menuItem, tone === 'danger' ? styles.menuItemDanger : '']
        .filter(Boolean)
        .join(' ')}
      disabled={disabled}
      onClick={() => {
        close?.();
        void onSelect();
      }}
    >
      {children}
    </button>
  );
}
